'use client';

import { c as _c } from "react/compiler-runtime";
import { MapPin, Plus, Search, Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { Button } from '~/components/Button';
import { Combobox, ComboboxItem, ComboboxItemIcon } from '~/components/Combobox';
import { Drawer } from '~/components/Drawer';
import { FlexibleDatesPanel } from '~/components/FlexibleDatesPanel';
import { PlaygroundDateSelector } from '~/components/Playground/PlaygroundDateSelector/PlaygroundDateSelector';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { usePushToMigratedRoute } from '~/hooks/usePushToMigratedRoute';
import type { Locale } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import { type DestinationSearchTerm, useDestinationSearch } from '~/stores/blueprintIndexStore';
import { usePlaygroundActions } from '~/stores/playgroundStore';
import { buildDestinationNameMaps, type DestinationNameMaps, getLocalisedDestinationLabel } from '~/utils/blueprintHelpers/destinationLabels';
import styles from './NavSearch.module.css';
function DestinationItem(t0) {
  const $ = _c(20);
  const {
    item,
    noResultsLabel,
    nameMaps
  } = t0;
  if (item.id === "no-results") {
    const t1 = `combobox-no-results-${item.id}`;
    let t2;
    if ($[0] !== noResultsLabel) {
      t2 = <span>{noResultsLabel}</span>;
      $[0] = noResultsLabel;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== item.id || $[3] !== t1 || $[4] !== t2) {
      t3 = <ComboboxItem id={item.id} key={t1} textValue="no-results">{t2}</ComboboxItem>;
      $[2] = item.id;
      $[3] = t1;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t1;
  if ($[6] !== item.destination || $[7] !== nameMaps) {
    t1 = getLocalisedDestinationLabel(item.destination, nameMaps);
    $[6] = item.destination;
    $[7] = nameMaps;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const label = t1;
  const t2 = `combobox-${item.destination}`;
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ComboboxItemIcon><MapPin decorative={true} size="large" /></ComboboxItemIcon>;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== label) {
    t4 = <div className={styles.itemContent}>{t3}<span className={styles.placeName}>{label}</span></div>;
    $[10] = label;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className={styles.addIcon}><Plus decorative={true} /></span>;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4) {
    t6 = <div className={styles.pinAndTextContainer}>{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== item.destination || $[16] !== item.id || $[17] !== t2 || $[18] !== t6) {
    t7 = <ComboboxItem id={item.id} key={t2} textValue={item.destination} data-ua-click="megamenu_search_destination_select">{t6}</ComboboxItem>;
    $[15] = item.destination;
    $[16] = item.id;
    $[17] = t2;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
}
export function NavSearch() {
  const $ = _c(150);
  const t = useTranslations();
  const locale = useLocale();
  const pushToMigratedRoute = usePushToMigratedRoute();
  const [isOpen, setIsOpen] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("");
  const [dateData, setDateData] = useState(null);
  const [isDateDrawerOpen, setIsDateDrawerOpen] = useState(false);
  const isDesktop = useIsDesktop(1300);
  const searchLabelId = useId();
  const {
    closeDrawer,
    setNights,
    setStartDate,
    setEndDate
  } = usePlaygroundActions();
  const t0 = locale as Locale;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      locale: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isOpen) {
    t2 = {
      enabled: isOpen
    };
    $[2] = isOpen;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: blueprints
  } = api.cms.blueprints.useQuery(t1, t2);
  const t3 = locale as Locale;
  let t4;
  if ($[4] !== t3) {
    t4 = {
      locale: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isOpen) {
    t5 = {
      enabled: isOpen
    };
    $[6] = isOpen;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data: countriesLocalised
  } = api.cms.countries.useQuery(t4, t5);
  let t6;
  if ($[8] !== isOpen) {
    t6 = {
      enabled: isOpen
    };
    $[8] = isOpen;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const {
    data: countriesEnglish
  } = api.cms.countries.useQuery(undefined, t6);
  const t7 = locale as Locale;
  let t8;
  if ($[10] !== t7) {
    t8 = {
      locale: t7
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== isOpen) {
    t9 = {
      enabled: isOpen
    };
    $[12] = isOpen;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const {
    data: placesLocalised
  } = api.cms.places.useQuery(t8, t9);
  let t10;
  if ($[14] !== isOpen) {
    t10 = {
      enabled: isOpen
    };
    $[14] = isOpen;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const {
    data: placesEnglish
  } = api.cms.places.useQuery(undefined, t10);
  const t11 = locale as Locale;
  let t12;
  if ($[16] !== t11) {
    t12 = {
      locale: t11
    };
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== isOpen) {
    t13 = {
      enabled: isOpen
    };
    $[18] = isOpen;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const {
    data: regionsLocalised
  } = api.cms.regions.useQuery(t12, t13);
  let t14;
  if ($[20] !== isOpen) {
    t14 = {
      enabled: isOpen
    };
    $[20] = isOpen;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const {
    data: regionsEnglish
  } = api.cms.regions.useQuery(undefined, t14);
  let t15;
  if ($[22] !== countriesEnglish || $[23] !== countriesLocalised || $[24] !== placesEnglish || $[25] !== placesLocalised || $[26] !== regionsEnglish || $[27] !== regionsLocalised) {
    t15 = buildDestinationNameMaps({
      countries: countriesLocalised ?? [],
      places: placesLocalised ?? [],
      regions: regionsLocalised ?? []
    }, {
      countries: countriesEnglish ?? [],
      places: placesEnglish ?? [],
      regions: regionsEnglish ?? []
    });
    $[22] = countriesEnglish;
    $[23] = countriesLocalised;
    $[24] = placesEnglish;
    $[25] = placesLocalised;
    $[26] = regionsEnglish;
    $[27] = regionsLocalised;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const nameMaps = t15;
  let t16;
  if ($[29] !== blueprints) {
    t16 = blueprints ?? [];
    $[29] = blueprints;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const searchResults = useDestinationSearch(t16, filterValue);
  let t17;
  if ($[31] !== closeDrawer || $[32] !== setEndDate || $[33] !== setNights || $[34] !== setStartDate) {
    t17 = () => {
      setIsOpen(true);
      setSelectedDestination("");
      setFilterValue("");
      setDateData(null);
      setIsDateDrawerOpen(false);
      closeDrawer();
      setNights(null);
      setStartDate(null);
      setEndDate(null);
    };
    $[31] = closeDrawer;
    $[32] = setEndDate;
    $[33] = setNights;
    $[34] = setStartDate;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const handleOpen = t17;
  let t18;
  if ($[36] !== closeDrawer) {
    t18 = () => {
      setIsOpen(false);
      setFilterValue("");
      setSelectedDestination("");
      setDateData(null);
      setIsDateDrawerOpen(false);
      closeDrawer();
    };
    $[36] = closeDrawer;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const handleClose = t18;
  let t19;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = data => {
      setDateData(data);
      if (data) {
        setIsDateDrawerOpen(false);
      }
    };
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const handleDateChange = t19;
  let t20;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      setIsDateDrawerOpen(true);
    };
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const handleDateSelectorClick = t20;
  let t21;
  if ($[40] !== dateData || $[41] !== handleClose || $[42] !== pushToMigratedRoute || $[43] !== selectedDestination) {
    t21 = destination => {
      const dest = destination || selectedDestination;
      if (!dest) {
        return;
      }
      const params = new URLSearchParams();
      params.set("destination", dest);
      if (dateData?.type === "calendar" && typeof dateData.value === "object") {
        params.set("startDate", dateData.value.startDate);
        const start = new Date(dateData.value.startDate);
        const end = new Date(dateData.value.endDate);
        const duration = Math.ceil((end.getTime() - start.getTime()) / 86400000);
        if (duration > 0) {
          params.set("duration", String(duration));
        }
      } else {
        if (dateData?.type === "nights" && typeof dateData.value === "number") {
          params.set("duration", String(dateData.value));
        }
      }
      pushToMigratedRoute(`/blueprint-index?${params.toString()}`);
      handleClose();
    };
    $[40] = dateData;
    $[41] = handleClose;
    $[42] = pushToMigratedRoute;
    $[43] = selectedDestination;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const navigateToBpi = t21;
  let t22;
  let t23;
  if ($[45] !== handleClose || $[46] !== isDateDrawerOpen || $[47] !== isOpen) {
    t22 = () => {
      if (!isOpen) {
        return;
      }
      const handleKeyDown = e => {
        if (e.key === "Escape" && !isDateDrawerOpen) {
          handleClose();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t23 = [isOpen, handleClose, isDateDrawerOpen];
    $[45] = handleClose;
    $[46] = isDateDrawerOpen;
    $[47] = isOpen;
    $[48] = t22;
    $[49] = t23;
  } else {
    t22 = $[48];
    t23 = $[49];
  }
  useEffect(t22, t23);
  let t24;
  if ($[50] !== navigateToBpi || $[51] !== selectedDestination) {
    t24 = () => {
      if (selectedDestination) {
        navigateToBpi();
      }
    };
    $[50] = navigateToBpi;
    $[51] = selectedDestination;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  const handleSubmit = t24;
  if (isDesktop) {
    let t25;
    if ($[53] !== handleClose || $[54] !== isOpen || $[55] !== t) {
      t25 = isOpen && <button type="button" className={styles.backdrop} onClick={handleClose} aria-label={t("aria.close_search")} tabIndex={-1} />;
      $[53] = handleClose;
      $[54] = isOpen;
      $[55] = t;
      $[56] = t25;
    } else {
      t25 = $[56];
    }
    let t26;
    if ($[57] !== isOpen) {
      t26 = clsx(styles.desktopSearch, {
        [styles.desktopSearchOpen]: isOpen
      });
      $[57] = isOpen;
      $[58] = t26;
    } else {
      t26 = $[58];
    }
    let t27;
    if ($[59] !== t) {
      t27 = t("navigation.search_destinations");
      $[59] = t;
      $[60] = t27;
    } else {
      t27 = $[60];
    }
    let t28;
    if ($[61] !== searchLabelId || $[62] !== t27) {
      t28 = <span id={searchLabelId} className="sr-only">{t27}</span>;
      $[61] = searchLabelId;
      $[62] = t27;
      $[63] = t28;
    } else {
      t28 = $[63];
    }
    let t29;
    if ($[64] !== t) {
      t29 = t("navigation.search_placeholder");
      $[64] = t;
      $[65] = t29;
    } else {
      t29 = $[65];
    }
    let t30;
    if ($[66] !== navigateToBpi || $[67] !== searchResults) {
      t30 = id => {
        if (id === null || id === "no-results") {
          setFilterValue("");
          return;
        }
        const matched = searchResults.find(term => term.id === id);
        if (!matched) {
          return;
        }
        navigateToBpi(matched.destination);
      };
      $[66] = navigateToBpi;
      $[67] = searchResults;
      $[68] = t30;
    } else {
      t30 = $[68];
    }
    let t31;
    if ($[69] !== filterValue || $[70] !== nameMaps || $[71] !== t) {
      t31 = item => <DestinationItem item={item} noResultsLabel={t("navigation.search_no_results", {
        query: filterValue
      })} nameMaps={nameMaps} />;
      $[69] = filterValue;
      $[70] = nameMaps;
      $[71] = t;
      $[72] = t31;
    } else {
      t31 = $[72];
    }
    let t32;
    if ($[73] !== filterValue || $[74] !== searchLabelId || $[75] !== searchResults || $[76] !== t29 || $[77] !== t30 || $[78] !== t31) {
      t32 = <div className={styles.desktopComboboxWrapper}><Combobox id="nav-destination-combobox" name="nav-destination" aria-labelledby={searchLabelId} inputValue={filterValue} items={searchResults} onInputChange={setFilterValue} placeholder={t29} border="none" formValue="text" shouldFlip={false} placement="bottom" autoFocus={true} onSelectionChange={t30}>{t31}</Combobox></div>;
      $[73] = filterValue;
      $[74] = searchLabelId;
      $[75] = searchResults;
      $[76] = t29;
      $[77] = t30;
      $[78] = t31;
      $[79] = t32;
    } else {
      t32 = $[79];
    }
    let t33;
    if ($[80] !== t) {
      t33 = t("aria.close_search");
      $[80] = t;
      $[81] = t33;
    } else {
      t33 = $[81];
    }
    let t34;
    if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <Xmark decorative={true} size="large" />;
      $[82] = t34;
    } else {
      t34 = $[82];
    }
    let t35;
    if ($[83] !== handleClose || $[84] !== t33) {
      t35 = <button type="button" className={styles.clearButton} onClick={handleClose} aria-label={t33} data-test-id="nav-search-close-desktop">{t34}</button>;
      $[83] = handleClose;
      $[84] = t33;
      $[85] = t35;
    } else {
      t35 = $[85];
    }
    let t36;
    if ($[86] !== t26 || $[87] !== t28 || $[88] !== t32 || $[89] !== t35) {
      t36 = <div className={t26} data-test-id="nav-search-desktop">{t28}{t32}{t35}</div>;
      $[86] = t26;
      $[87] = t28;
      $[88] = t32;
      $[89] = t35;
      $[90] = t36;
    } else {
      t36 = $[90];
    }
    let t37;
    if ($[91] !== handleOpen || $[92] !== isOpen || $[93] !== t) {
      t37 = !isOpen && <button type="button" className={styles.searchIconButton} onClick={handleOpen} aria-label={t("aria.trips_search")} data-ua-click="megamenu_search_open" data-test-id="nav-search-icon"><Search size="large" decorative={true} /></button>;
      $[91] = handleOpen;
      $[92] = isOpen;
      $[93] = t;
      $[94] = t37;
    } else {
      t37 = $[94];
    }
    let t38;
    if ($[95] !== t25 || $[96] !== t36 || $[97] !== t37) {
      t38 = <div className={styles.searchContainer} data-test-id="nav-search-container">{t25}{t36}{t37}</div>;
      $[95] = t25;
      $[96] = t36;
      $[97] = t37;
      $[98] = t38;
    } else {
      t38 = $[98];
    }
    return t38;
  }
  let t25;
  if ($[99] !== t) {
    t25 = t("aria.trips_search");
    $[99] = t;
    $[100] = t25;
  } else {
    t25 = $[100];
  }
  let t26;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Search size="large" decorative={true} />;
    $[101] = t26;
  } else {
    t26 = $[101];
  }
  let t27;
  if ($[102] !== handleOpen || $[103] !== t25) {
    t27 = <button type="button" className={styles.searchIconButton} onClick={handleOpen} aria-label={t25} data-ua-click="megamenu_search_open" data-test-id="nav-search-icon">{t26}</button>;
    $[102] = handleOpen;
    $[103] = t25;
    $[104] = t27;
  } else {
    t27 = $[104];
  }
  let t28;
  if ($[105] !== handleClose) {
    t28 = open => !open && handleClose();
    $[105] = handleClose;
    $[106] = t28;
  } else {
    t28 = $[106];
  }
  let t29;
  if ($[107] !== t) {
    t29 = t("aria.trips_search");
    $[107] = t;
    $[108] = t29;
  } else {
    t29 = $[108];
  }
  let t30;
  if ($[109] !== t) {
    t30 = t("aria.close_search");
    $[109] = t;
    $[110] = t30;
  } else {
    t30 = $[110];
  }
  let t31;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Xmark decorative={true} size="large" />;
    $[111] = t31;
  } else {
    t31 = $[111];
  }
  let t32;
  if ($[112] !== handleClose || $[113] !== t30) {
    t32 = <div className={styles.mobileOverlayHeader}><button type="button" className={styles.mobileCloseButton} onClick={handleClose} aria-label={t30} data-test-id="nav-search-close-mobile">{t31}</button></div>;
    $[112] = handleClose;
    $[113] = t30;
    $[114] = t32;
  } else {
    t32 = $[114];
  }
  let t33;
  if ($[115] !== filterValue || $[116] !== nameMaps || $[117] !== searchResults || $[118] !== selectedDestination || $[119] !== t) {
    t33 = <div className={styles.mobileDestinationField}>{selectedDestination ? <div className={styles.drawerSelectedDestination}><span>{getLocalisedDestinationLabel(selectedDestination, nameMaps)}</span><Button ariaLabel={t("aria.clear_destination")} variant="icon" onPress={() => {
          setSelectedDestination("");
          setFilterValue("");
        }}><Xmark size="large" decorative={true} /></Button></div> : <Combobox id="nav-mobile-destination-combobox" name="nav-mobile-destination" label={t("navigation.places")} inputValue={filterValue} items={searchResults} onInputChange={setFilterValue} placeholder={t("navigation.search_placeholder_mobile")} border="default" formValue="text" shouldFlip={false} placement="bottom" autoFocus={true} onSelectionChange={id_0 => {
        if (id_0 === null || id_0 === "no-results") {
          setFilterValue("");
          return;
        }
        const matched_0 = searchResults.find(term_0 => term_0.id === id_0);
        if (!matched_0) {
          return;
        }
        setSelectedDestination(matched_0.destination ?? "");
        setFilterValue("");
      }}>{item_0 => <DestinationItem item={item_0} noResultsLabel={t("navigation.search_no_results", {
          query: filterValue
        })} nameMaps={nameMaps} />}</Combobox>}</div>;
    $[115] = filterValue;
    $[116] = nameMaps;
    $[117] = searchResults;
    $[118] = selectedDestination;
    $[119] = t;
    $[120] = t33;
  } else {
    t33 = $[120];
  }
  let t34;
  if ($[121] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <div className={styles.drawerField}><PlaygroundDateSelector dataUaClick="megamenu_search_when" onChange={handleDateChange} onClickOverride={handleDateSelectorClick} /></div>;
    $[121] = t34;
  } else {
    t34 = $[121];
  }
  let t35;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => setIsDateDrawerOpen(false);
    $[122] = t35;
  } else {
    t35 = $[122];
  }
  let t36;
  if ($[123] !== t) {
    t36 = t("aria.select_dates");
    $[123] = t;
    $[124] = t36;
  } else {
    t36 = $[124];
  }
  let t37;
  if ($[125] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <FlexibleDatesPanel layout="vertical" />;
    $[125] = t37;
  } else {
    t37 = $[125];
  }
  let t38;
  if ($[126] !== isDateDrawerOpen || $[127] !== t36) {
    t38 = <Drawer isOpen={isDateDrawerOpen} close={t35} ariaLabel={t36} noHeader={true}>{t37}</Drawer>;
    $[126] = isDateDrawerOpen;
    $[127] = t36;
    $[128] = t38;
  } else {
    t38 = $[128];
  }
  const t39 = !selectedDestination;
  let t40;
  if ($[129] !== t) {
    t40 = t("navigation.search");
    $[129] = t;
    $[130] = t40;
  } else {
    t40 = $[130];
  }
  let t41;
  if ($[131] !== handleSubmit || $[132] !== t39 || $[133] !== t40) {
    t41 = <div className={styles.drawerSubmitButton}><Button variant="primary" size="fullWidth" onPress={handleSubmit} isDisabled={t39} data-ua-click="megamenu_search_submit" data-test-id="nav-search-submit-mobile">{t40}</Button></div>;
    $[131] = handleSubmit;
    $[132] = t39;
    $[133] = t40;
    $[134] = t41;
  } else {
    t41 = $[134];
  }
  let t42;
  if ($[135] !== t33 || $[136] !== t38 || $[137] !== t41) {
    t42 = <div className={styles.mobileOverlayContent}>{t33}{t34}{t38}{t41}</div>;
    $[135] = t33;
    $[136] = t38;
    $[137] = t41;
    $[138] = t42;
  } else {
    t42 = $[138];
  }
  let t43;
  if ($[139] !== t29 || $[140] !== t32 || $[141] !== t42) {
    t43 = <Modal className={styles.mobileModal}><Dialog aria-label={t29} className={styles.mobileDialog} data-test-id="nav-search-mobile">{t32}{t42}</Dialog></Modal>;
    $[139] = t29;
    $[140] = t32;
    $[141] = t42;
    $[142] = t43;
  } else {
    t43 = $[142];
  }
  let t44;
  if ($[143] !== isOpen || $[144] !== t28 || $[145] !== t43) {
    t44 = <ModalOverlay className={styles.mobileOverlay} isOpen={isOpen} onOpenChange={t28}>{t43}</ModalOverlay>;
    $[143] = isOpen;
    $[144] = t28;
    $[145] = t43;
    $[146] = t44;
  } else {
    t44 = $[146];
  }
  let t45;
  if ($[147] !== t27 || $[148] !== t44) {
    t45 = <>{t27}{t44}</>;
    $[147] = t27;
    $[148] = t44;
    $[149] = t45;
  } else {
    t45 = $[149];
  }
  return t45;
}